<template>
  <div class="pc">
    <FilterBar v-if="(spec.filters || []).length" :filters="spec.filters" :values="values" @change="setFilter" />
    <div class="pc-stagebg" :style="stageStyle(spec)">
      <div ref="viewport" class="pc-viewport" :style="{ height: stageHeight + 'px' }">
      <div class="pc-canvas" :style="canvasStyle">
        <div v-for="p in spec.placements || []" :key="p.id" class="pc-place" :data-placement="p.id" :style="placeStyle(p, layouts[p.id])">
          <WidgetHost
            :placement="p"
            :widget="widgets[p.widget]"
            :page-id="pageId"
            :session-id="sessionId"
            :filters="values"
            :preloaded="preloaded ? preloaded[p.id] || {} : null"
            :spec="spec"
            :host="host"
            :width="p.layout.w"
            :height="layouts[p.id].h"
            :grow="!!p.layout.grow"
            @content-height="measure(p.id, $event)"
            @drill-entity="$emit('drill-entity', $event)"
            @select-patient="$emit('select-patient', $event)"
          />
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import FilterBar from './FilterBar.vue';
import WidgetHost from './WidgetHost.vue';
import { canvasHeight, canvasWidth, filterDefaults, flowLayouts, placeStyle, stageStyle } from './pageLayout';

export default {
  name: 'PageCanvas',
  components: { FilterBar, WidgetHost },
  provide() {
    return { pageRows: this.cachedRows, forgetSources: this.forgetSources };
  },
  props: {
    pageId: { type: Number, default: null },
    sessionId: { type: String, default: '' },
    spec: { type: Object, required: true },
    widgets: { type: Object, default: () => ({}) },
    preloaded: { type: Object, default: null },
    host: { type: Object, default: () => ({}) },
  },
  emits: ['drill-entity', 'select-patient'],
  data() {
    return { values: filterDefaults(this.spec), viewportWidth: 0, measured: {} };
  },
  computed: {
    scale() {
      if (!this.viewportWidth) return 1;
      return this.viewportWidth / canvasWidth(this.spec);
    },
    layouts() {
      return flowLayouts(this.spec, this.measured);
    },
    stageHeight() {
      return Math.ceil(canvasHeight(this.spec, this.layouts) * this.scale);
    },
    canvasStyle() {
      return {
        width: canvasWidth(this.spec) + 'px',
        height: canvasHeight(this.spec, this.layouts) + 'px',
        transform: `scale(${this.scale})`,
      };
    },
  },
  watch: {
    spec(next) {
      this.values = filterDefaults(next);
      this.rowsCache.clear();
    },
    values: { deep: true, handler() { this.rowsCache.clear(); } },
    host: { deep: true, handler() { this.rowsCache.clear(); } },
  },
  created() {
    this.rowsCache = new Map();
  },
  mounted() {
    this.measureViewport();
    window.addEventListener('resize', this.measureViewport);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.measureViewport);
  },
  methods: {
    placeStyle,
    stageStyle,
    setFilter(name, value) {
      this.values = { ...this.values, [name]: value };
    },
    cachedRows(key, load) {
      if (!this.rowsCache.has(key)) this.rowsCache.set(key, load());
      return this.rowsCache.get(key);
    },
    forgetSources(names, scope) {
      names.forEach((name) => this.rowsCache.delete(`${name}|${JSON.stringify(scope)}`));
    },
    measureViewport() {
      this.viewportWidth = this.$refs.viewport ? this.$refs.viewport.clientWidth : 0;
    },
    measure(id, height) {
      if (Math.abs((this.measured[id] || 0) - height) > 1) this.measured = { ...this.measured, [id]: height };
    },
  },
};
</script>

<style scoped>
.pc { display: flex; flex-direction: column; gap: 10px; }
.pc-viewport { position: relative; overflow: hidden; }
.pc-canvas { position: absolute; left: 0; top: 0; transform-origin: 0 0; }
.pc-place { position: absolute; }
</style>
